import CashBackBannerIcon from "@Icons/CashbackBannerIcon";
import FileFilledIcon from "@Icons/FileFilledIcon";
import FromPolicies from "./FromPolicies";
import FormOffers from "./FormOffers";
import BookingIcon from "@Components/Stepper/icons/BookingIcon";
import PaymentIcon from "@Components/Stepper/icons/PaymentIcon";
import BookingConfirmationIcon from "@Components/Stepper/icons/BookingConfirmationIcon";
import saveStepwiseData from "@Pages/BookForm/data/saveStepwiseData";
import fetchSavedBookformData from "@Pages/BookForm/data/fetchSavedBookformData";
import { getSavedValues } from "@Pages/BookForm/utils";
import {
  emergencyDetailsKeys,
  guarantorDetailsKeys,
  personalDetailsKeys,
} from "@Pages/BookForm/data/defaultFieldKeys";
import BookFormDispatcher from "@Pages/BookForm/action";
import initiateBookformAPI from "@Pages/BookForm/data/initiateBookFormAPI";
import config from "@Config/index";
import InstalmentSection from "@Pages/PropertyPage/components/InstalmentSection";
import currencyData from "constants/currencyConstants";

/* eslint-disable import/prefer-default-export */
type FormatNotificationItemsType = {
  items: {
    offers: any;
    cancellationPolicies: any;
    bookingDisclaimers: any;
    paymentPolicies: any;
  };
  isNewUSPSection?: boolean;
  isMobile?: boolean;
  isPaymentForm?: boolean;
  t?: any;
  inventoryData?: any;
  installmentPlans?: any[];
};

const getInventoryNotificationItemsData = (data: any) => {
  const offers = config.IS_WHITELABEL_APP ? [] : data?.offers || data?.active_offers || [];

  const currentPaymentType: string = data?.custom_nationality_payment || data?.meta?.payment_type;

  const listedPaymentTypes =
    data?.payment_types?.flatMap((type: string) =>
      type === "deposit" ? ["booking_deposit", "security_deposit"] : type,
    ) || null;

  const paymentTypeTagMap = {
    refundable_token: "fully_refundable_holding_fee",
    deposit: "booking_deposit",
    platform_fee: "platform_fee",
  };

  const paymentTypes = ["fully_refundable_holding_fee", "booking_deposit", "platform_fee"];

  const cancellationPolicies =
    data?.description?.filter(
      (item: any) =>
        item?.name === "cancellation_policy" || item?.name_en === "cancellation_policy",
    ) || [];

  const bookingDisclaimers =
    data?.description?.filter(
      (item: any) => item?.name === "booking_disclaimer" || item?.name_en === "booking_disclaimer",
    ) || [];

  let paymentPolicies =
    data?.description?.filter(
      (item: any) => item?.name === "payment" || item?.name_en === "payment",
    ) || [];

  if (paymentPolicies.length) {
    const paymentTypesSet = new Set(paymentTypes);
    const listedTypesSet = listedPaymentTypes ? new Set(listedPaymentTypes) : null;
    const currentTag = paymentTypeTagMap[currentPaymentType as keyof typeof paymentTypeTagMap];

    paymentPolicies = paymentPolicies.filter((policy: any) => {
      if (listedTypesSet?.has(policy.tag)) return true;
      if (policy.tag !== currentTag && paymentTypesSet.has(policy.tag)) return false;
      return true;
    });
  }

  return { offers, cancellationPolicies, bookingDisclaimers, paymentPolicies };
};

const isArrayNonEmpty = (arr: any) => Array.isArray(arr) && arr.length;

const getBillsAndPaymentsData = (inventoryData: any, installmentPlans: any[] = []) => {
  if (!inventoryData) return [];
  const finalInstallmentPlans =
    installmentPlans?.length > 0 ? installmentPlans : inventoryData?.installment_plans || [];

  // const features = inventoryData?.features || [];
  // const billsIncludedFeatures =
  //   features?.filter(
  //     (amenity: any) =>
  //       ["Bills Included"].includes(amenity.name_en || amenity.name) && amenity.values?.length > 0,
  //   ) || [];

  const hasInstallmentPlans = finalInstallmentPlans?.length > 0;
  // const hasBillsIncluded = billsIncludedFeatures?.length > 0;

  if (hasInstallmentPlans) {
    return [{ type: "bills_and_payments", hasInstallmentPlans }];
  }

  return [];
};

const formatNotificationItems = ({
  items: { offers, cancellationPolicies, bookingDisclaimers, paymentPolicies },
  isMobile,
  t,
  inventoryData,
  installmentPlans = [],
}: FormatNotificationItemsType) =>
  [
    isArrayNonEmpty(offers) && {
      title: `${t("bookform.formlayout.offerstitle")} (${offers.length})`,
      color: "info.5",
      listIcon: <CashBackBannerIcon fill="#fdc8d3" stroke="#1f2a37" fillCircle="#fff" />,
      content: <FormOffers offers={offers} />,
    },
    isArrayNonEmpty(cancellationPolicies) &&
      cancellationPolicies.length && {
        title: isMobile
          ? `${t("bookform.formlayout.cancellationtitle")}`
          : `${t("bookform.formlayout.cancellationpolicytitle")} (${cancellationPolicies.length})`,
        color: "neutral.5",
        listIcon: <FileFilledIcon />,
        content: <FromPolicies policies={cancellationPolicies} />,
        titleForLogic: "cancellation",
      },
    isArrayNonEmpty(paymentPolicies) &&
      paymentPolicies.length && {
        title: isMobile
          ? `${t("bookform.formlayout.paymenttitle")}`
          : `${t("bookform.formlayout.paymentpolicytitle")} (${paymentPolicies.length})`,
        color: "primary.5",
        listIcon: <FileFilledIcon />,
        content: <FromPolicies policies={paymentPolicies} />,
      },

    isArrayNonEmpty(bookingDisclaimers) &&
      bookingDisclaimers.length && {
        title: `${t("bookform.formlayout.disclaimertitle")}`,
        color: "warning.5",
        leftIcon: "info",
        content: bookingDisclaimers[0]?.value,
      },

    isArrayNonEmpty(getBillsAndPaymentsData(inventoryData, installmentPlans)) && {
      title: `${t("bookform.formlayout.billsandpaymentstitle")}`,
      color: "primary.5",
      listIcon: <FileFilledIcon />,
      content: <InstalmentSection isDesktop={!isMobile} isModal />,
    },
  ].filter(Boolean);

const BookingStep = ({ isDesktop, isApplicationFormFlowEnabled, t }: any) => {
  let title = "";
  if (isDesktop) {
    title = isApplicationFormFlowEnabled
      ? t("bookform.formlayout.stepper.personaldetails")
      : t("bookform.formlayout.stepper.bookingdetails");
  } else {
    title = t("bookform.formlayout.stepper.details");
  }
  return {
    title: `${title}`,
    icon: (fill: string) => <BookingIcon fill={fill} />,
  };
};

const PaymentStep = (t: any) => ({
  title: t("bookform.formlayout.paymenttitle"),
  icon: (fill: string) => <PaymentIcon fill={fill} />,
});

const ConfirmationStep = ({ isDesktop, isApplicationFormFlowEnabled, t }: any) => {
  let title = t("bookform.formlayout.stepperconfirmation");
  if (isApplicationFormFlowEnabled) title = t("bookform.formlayout.stepperapplication");
  if (!isApplicationFormFlowEnabled && isDesktop)
    title = t("bookform.formlayout.stepperbookingconfirmation");
  return {
    title,
    icon: (fill: string) => <BookingConfirmationIcon fill={fill} />,
  };
};

const getStepperData = (
  excludePayment: boolean,
  isDesktop: boolean,
  isPaymentBeforeBookform: boolean,
  isApplicationFormFlowEnabled: boolean,
  t: any,
) => {
  let requiredStepperData = [];
  const isApplicationFormFlow = isApplicationFormFlowEnabled && !config.IS_CHINA_APP;
  if (isPaymentBeforeBookform) {
    requiredStepperData = [
      PaymentStep(t),
      BookingStep({
        isDesktop,
        isApplicationFormFlowEnabled: isApplicationFormFlow,
        t,
      }),
      ConfirmationStep({
        isDesktop,
        isApplicationFormFlowEnabled: isApplicationFormFlow,
        t,
      }),
    ];
  } else {
    requiredStepperData = [
      BookingStep({
        isDesktop,
        isApplicationFormFlowEnabled: isApplicationFormFlow,
        t,
      }),
      PaymentStep(t),
      ConfirmationStep({
        isDesktop,
        isApplicationFormFlowEnabled: isApplicationFormFlow,
        t,
      }),
    ];
  }

  if (excludePayment) {
    return requiredStepperData
      .filter((data) => data.title !== "Payment" && !(config.IS_CHINA_APP && data.title === "支付"))
      .map((step, idx) => ({ ...step, id: idx + 1 }));
  }
  return requiredStepperData.map((step, idx) => ({ ...step, id: idx + 1 }));
};

const getHeadline = (
  isPaymentPage: boolean,
  userName = "",
  isDesktop: boolean,
  isPaymentBeforeBookform: boolean,
  heading: string,
  strongHeading: string,
  t: any,
  subHeadline = "bookform.formlayout.headlineDefaultSub",
  isApplicationFormFlowEnabled: boolean,
  isWhitelabel: boolean,
) => {
  if (isApplicationFormFlowEnabled) {
    return {
      headline: (
        <>
          <strong>{t("bookform.formlayout.headlineapplicationform")}</strong>
        </>
      ),
      ...(!isPaymentBeforeBookform && {
        subHeadline: t("bookform.formlayout.headlineapplicationformsub"),
      }),
    };
  }

  if (!isPaymentPage) {
    return {
      headline: (
        <h3 className={isWhitelabel ? "wl-title-font" : ""}>
          <strong>{strongHeading}</strong> {heading}.
        </h3>
      ),
      ...(!isPaymentBeforeBookform && {
        subHeadline,
      }),
    };
  }
  return {
    headline: (
      <span
        style={{
          fontSize: isDesktop ? "20px" : "16px",
          lineHeight: "150%",
          fontWeight: "400",
          color: "#111928",
        }}
      >
        {isPaymentBeforeBookform ? (
          <>
            <strong style={{ fontWeight: "700" }}>{`${t(
              t("bookform.formlayout.headlinegreat"),
            )} ${userName}!`}</strong>{" "}
            {t("bookform.formlayout.headlinealmostbooked")}
          </>
        ) : (
          <>
            <strong style={{ fontWeight: "700" }}>{`${t(
              t("bookform.formlayout.headlinehey"),
            )} ${userName},`}</strong>{" "}
            {t("bookform.formlayout.headlinealmostbookedfinalstep")}
          </>
        )}
      </span>
    ),
    ...(!isPaymentBeforeBookform && {
      subHeadline: t("bookform.formlayout.headlinefasttrackpayment"),
    }),
  };
};

const getAccommodationCardFormattedData = ({
  formValues = {},
  accommodationDetails = {},
}: {
  formValues: any;
  accommodationDetails: any;
}) => {
  let arrivalDate = formValues?.arrival_date || accommodationDetails?.arrival_date;
  if (arrivalDate) {
    const dateObj = new Date(arrivalDate);
    [arrivalDate] = dateObj.toISOString().split("T");
  }

  const leaseDuration = formValues?.duration || accommodationDetails?.duration;

  return {
    roomType: +formValues?.roomType || +accommodationDetails?.roomType,
    duration: leaseDuration,
    custom_duration: formValues?.custom_duration || accommodationDetails?.custom_duration,
    inventory_id: +formValues?.inventory_id || +accommodationDetails?.inventory_id,
    arrival_date: arrivalDate,
    moveOutDate: formValues?.moveOutDate || accommodationDetails?.moveOutDate,
    ...(leaseDuration !== "flexible" && {
      tenancyId: +formValues?.tenancyId || +accommodationDetails?.tenancyId,
    }),
  };
};

const prefillFormDataFromSaved = ({
  data,
  dispatch,
  personalDetails,
  emergencyDetails,
  guarantorDetails,
}: any) => {
  const savedpersonalDetails = getSavedValues(personalDetailsKeys, data);
  const savedEmergencyDetails = getSavedValues(emergencyDetailsKeys, data);
  const savedGuarantorDetails = getSavedValues(guarantorDetailsKeys, data);

  const bookFormDispatcher = new BookFormDispatcher(dispatch);
  bookFormDispatcher.updateFormData({
    key: "personalDetails",
    formData: { ...personalDetails, ...savedpersonalDetails },
  });
  bookFormDispatcher.updateFormData({
    key: "emergencyDetails",
    formData: { ...emergencyDetails, ...savedEmergencyDetails },
  });
  bookFormDispatcher.updateFormData({
    key: "guarantorDetails",
    formData: { ...guarantorDetails, ...savedGuarantorDetails },
  });
};

const fetchAndSavePartialFormData = async ({
  formValues,
  accommodationDetails,
  isPaymentBeforeBookform,
  isPaymentPage,
  dispatch,
  personalDetails,
  emergencyDetails,
  guarantorDetails,
  shouldSaveData = true,
  source,
  book_form_completed = [],
  isContactDetailsSubmit = false,
}: any) => {
  const accDetailsFormattedData = getAccommodationCardFormattedData({
    formValues,
    accommodationDetails,
  });
  if (!isPaymentPage) {
    if (!config.IS_CHINA_APP && !book_form_completed.includes(accDetailsFormattedData.inventory_id))
      initiateBookformAPI(accDetailsFormattedData.inventory_id, source);

    let savedData: any = null;

    // For China: skip initial fetch.
    // Instead, save first, then fetch after token stabilizes.
    if (!config.IS_CHINA_APP) {
      const { message, savedData: fetchedData } = await fetchSavedBookformData({
        roomType: accDetailsFormattedData.roomType,
        tenancyId: accDetailsFormattedData?.tenancyId,
        inventory_id: accDetailsFormattedData.inventory_id,
      });

      if (message === "success" && fetchedData) {
        savedData = fetchedData;
        prefillFormDataFromSaved({
          // On ContactDetails submit, filter out user_details/* so the stale saved values don't
          // overwrite what the user just entered (same as the China fetch below)
          data: isContactDetailsSubmit
            ? Object.fromEntries(
                Object.entries(fetchedData).filter(([key]) => !key.startsWith("user_details/")),
              )
            : fetchedData,
          dispatch,
          personalDetails,
          // Reset base so saved values fully replace any stale/partial
          // emergency/guarantor data already in the store (original behavior).
          emergencyDetails: {},
          guarantorDetails: {},
        });
      }
    }

    const onlyLeadData = {
      "user_details/name": personalDetails["user_details/name"],
      "user_details/phone_code": personalDetails["user_details/phone_code"],
      "user_details/phone": personalDetails["user_details/phone"],
      "user_details/email": personalDetails["user_details/email"],
    };

    if (shouldSaveData) {
      await saveStepwiseData({
        data: {
          ...accDetailsFormattedData,
          ...(savedData
            ? onlyLeadData
            : { ...personalDetails, ...emergencyDetails, ...guarantorDetails }),
        },
        isPaymentBeforeBookform,
        stepName: "Accommodation details",
      });

      // For China: fetch after POST only on ContactDetails submit (token is now email based)
      if (config.IS_CHINA_APP && isContactDetailsSubmit) {
        const { message: chinaMessage, savedData: chinaSavedData } = await fetchSavedBookformData({
          roomType: accDetailsFormattedData.roomType,
          tenancyId: accDetailsFormattedData?.tenancyId,
          inventory_id: accDetailsFormattedData.inventory_id,
        });

        if (chinaMessage === "success" && chinaSavedData) {
          // Filter out user_details/* keys to avoid overwriting again ContactDetails data for china
          const filteredData = Object.fromEntries(
            Object.entries(chinaSavedData).filter(([key]) => !key.startsWith("user_details/")),
          );
          prefillFormDataFromSaved({
            data: filteredData,
            dispatch,
            personalDetails,
            emergencyDetails,
            guarantorDetails,
          });
        }

        // For China: call initiateBookformAPI after partial forms (token stabilized)
        if (!book_form_completed.includes(accDetailsFormattedData.inventory_id))
          initiateBookformAPI(accDetailsFormattedData.inventory_id, source);
      }
    }
  }
};

const updateSearchQueryParams = (history: any, updates: { [key: string]: any }) => {
  const url = new URL(window?.location?.href);
  const searchParams = new URLSearchParams(url.search);

  Object.entries(updates).forEach(([key, value]) => {
    if (value) searchParams.set(key, String(value));
    else searchParams.delete(key);
  });

  history.replace({ search: searchParams.toString() });
};

/**
 * Checks if a selected date falls within a specified range of any available dates
 * @param selectedDate - The date to check in DD-MM-YYYY format
 * @param availableDates - Array of available dates in DD-MM-YYYY format to check against
 * @param rangeDays - Number of days before and after each available date to include in range (default: 15)
 * @returns boolean - True if selected date is within range of any available date, false otherwise
 */
const isDateWithinMoveInRange = (
  selectedDate: string,
  availableDates: string[],
  rangeDays = 15,
): boolean => {
  if (!selectedDate || !Array.isArray(availableDates) || availableDates.length === 0) {
    return false;
  }

  const parseDate = (dateStr: string): number => {
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day).getTime();
  };

  const selectedTimestamp = parseDate(selectedDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = today.getTime();

  if (selectedTimestamp < todayTimestamp) {
    return false;
  }

  const dayInMs = 24 * 60 * 60 * 1000;
  const rangeInMs = rangeDays * dayInMs;

  return availableDates.some((availableDate) => {
    const availableTimestamp = parseDate(availableDate);
    return Math.abs(selectedTimestamp - availableTimestamp) < rangeInMs;
  });
};

const findCurrency = (currency?: string) =>
  currencyData.find(
    ({ standard_name, short_name }) =>
      standard_name?.toLowerCase() === currency?.toLowerCase() ||
      short_name?.toLowerCase() === currency?.toLowerCase(),
  );

/**
 * Resolves the featured image path shown on the book-form side card.
 *
 * When the form is opened at the root inventory (no specific room selected) and
 * the backend provides `meta.featured_image_from_config`, that config-level image
 * is shown so the book-form matches the property overview page. In every other
 * case (a room is selected, or no config image is present) the existing fallback
 * applies: the selected room's featured/first image, else the property's
 * featured/first image.
 */
const getFeaturedImageForCard = ({
  inventoryData,
  roomName,
}: {
  inventoryData: any;
  roomName?: string;
}): string => {
  const selectedRoom =
    roomName && inventoryData?.active_children?.find((room: any) => room.name === roomName);

  // Root inventory (no room selected): prefer the config-level featured image if present.
  if (!selectedRoom) {
    const featuredImageFromConfig = inventoryData?.meta?.featured_image_from_config;
    if (featuredImageFromConfig?.path) return featuredImageFromConfig.path;
  }

  const images = selectedRoom?.images?.length > 0 ? selectedRoom.images : inventoryData?.images;
  const featuredImg = images?.find((img: any) => img.featured) || images?.[0];
  return featuredImg?.path || "";
};

export {
  getInventoryNotificationItemsData,
  formatNotificationItems,
  getStepperData,
  getHeadline,
  getAccommodationCardFormattedData,
  fetchAndSavePartialFormData,
  updateSearchQueryParams,
  isDateWithinMoveInRange,
  findCurrency,
  getFeaturedImageForCard,
};
